import type { TeamRepoScore } from '@sdd-maturity/shared-types';
import { DELIVERY_ACTIVITY_WINDOW_DAYS } from '@sdd-maturity/shared-types';

export function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffDays = Math.floor((now - then) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 30) return `${diffDays} days ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

/** Most recent (max) of a list of ISO timestamps, ignoring empty/invalid values.
 *  Used to derive a single "last scanned" for a team from its per-repo /
 *  per-feature scan timestamps. Returns null when nothing valid is present. */
export function mostRecentTimestamp(dates: Array<string | undefined | null>): string | null {
  let bestMs = -Infinity;
  let bestStr: string | null = null;
  for (const d of dates) {
    if (!d) continue;
    const ms = new Date(d).getTime();
    if (!Number.isNaN(ms) && ms > bestMs) {
      bestMs = ms;
      bestStr = d;
    }
  }
  return bestStr;
}

export function isStale(dateStr: string): boolean {
  const diffDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  return diffDays > DELIVERY_ACTIVITY_WINDOW_DAYS;
}

export interface ClassifiedRepos {
  active: TeamRepoScore[];
  inactive: TeamRepoScore[];
  inaccessible: TeamRepoScore[];
  evalFailed: TeamRepoScore[];
  /** Repos classified as maintenance-only or infra-only (AC-6.1) */
  notScored: TeamRepoScore[];
}

/**
 * Frameworks whose repos never count toward Delivery scoring. Distinct from the
 * maintenance-only / infra-only frameworks, which `repoStatus` routes to the
 * `notScored` bucket rather than being filtered out of the active/scored set.
 */
export const EXCLUDED_FRAMEWORKS = new Set(['stale', 'evaluation-failed', 'inaccessible']);

/**
 * The one definition of "does this repo count toward Delivery scoring." A repo is
 * active when it has a real score, an included framework, was not skipped, and
 * was actually scanned — a SLED component→repo mapping can resolve a repo that
 * has never been scored, which `sledTeamReposHandler` stubs to `overallScore: 0,
 * lastScanTimestamp: ''` rather than omitting; without the timestamp check those
 * zero-score phantoms count as "active" and drag the team average down.
 * Consumed by both the rollup and standings (deliveryAvg) so they can no longer
 * drift apart, and consistent with the `active` bucket from classifyRepos.
 */
export function isActiveRepo(r: TeamRepoScore): boolean {
  return (
    r.overallScore >= 0 &&
    !EXCLUDED_FRAMEWORKS.has(r.framework) &&
    r.overallLevel !== 'SKIPPED' &&
    r.lastScanTimestamp !== ''
  );
}

/** Discriminated repo status — the single source of truth for how a repo is
 *  classified, labelled, badged, and sorted. Consumed by `classifyRepos` (bucket
 *  routing), `RepoTable` (row dispatch + status cell + sort), and `RepoCard`. */
export type RepoStatusKind = 'active' | 'inactive' | 'evalFailed' | 'notScored' | 'inaccessible';

export interface RepoStatus {
  status: RepoStatusKind;
  label: string;
  badgeClass: string;
  /** Sort ordinal — groups like statuses together in the "Other repos" list and
   *  the Status column. Preserved exactly from the former RepoTable.getRepoStatus. */
  ordinal: number;
}

/**
 * The one derivation of a repo's status, label, badge class, and sort ordinal.
 * Frameworks are mutually exclusive strings, so the framework checks below never
 * overlap; the score/level fallbacks then decide active vs eval-failed vs inactive.
 * `classifyRepos` routes its buckets off the `status` discriminant so the two can
 * no longer drift.
 */
export function repoStatus(repo: TeamRepoScore): RepoStatus {
  const level = repo.overallLevel;
  if (repo.framework === 'inaccessible') return { status: 'inaccessible', label: 'Not Accessible', badgeClass: 'badge-warning', ordinal: 5 };
  if (repo.framework === 'evaluation-failed') return { status: 'evalFailed', label: 'Eval Failed', badgeClass: 'badge-error', ordinal: 4 };
  if (repo.framework === 'infra-only') return { status: 'notScored', label: 'Infra Only', badgeClass: 'badge-infra', ordinal: 3 };
  if (repo.framework === 'maintenance-only') return { status: 'notScored', label: 'Maintenance Only', badgeClass: 'badge-maintenance', ordinal: 2 };
  if (repo.framework === 'stale' || level === 'SKIPPED') return { status: 'inactive', label: 'Inactive', badgeClass: 'badge-warning', ordinal: 1 };
  if (repo.overallScore === -1) return { status: 'evalFailed', label: 'Eval Failed', badgeClass: 'badge-error', ordinal: 4 };
  // A SLED-mapped repo that has never been scanned (no `lastScanTimestamp`) —
  // distinct from `stale`/`SKIPPED`, which imply a real prior scan. Kept out of
  // `active` for the same reason as `isActiveRepo` above.
  if (!repo.lastScanTimestamp) return { status: 'inactive', label: 'Not Scanned', badgeClass: 'badge-warning', ordinal: 1 };
  return { status: 'active', label: 'Active', badgeClass: 'badge-success', ordinal: 0 };
}

export function classifyRepos(repos: TeamRepoScore[]): ClassifiedRepos {
  const active: TeamRepoScore[] = [];
  const inactive: TeamRepoScore[] = [];
  const inaccessible: TeamRepoScore[] = [];
  const evalFailed: TeamRepoScore[] = [];
  const notScored: TeamRepoScore[] = [];

  for (const repo of repos) {
    // Route each repo off the single discriminated classifier. `notScored` covers
    // the maintenance-only / infra-only frameworks (AC-6.1).
    switch (repoStatus(repo).status) {
      case 'inaccessible':
        inaccessible.push(repo);
        break;
      case 'evalFailed':
        evalFailed.push(repo);
        break;
      case 'notScored':
        notScored.push(repo);
        break;
      case 'inactive':
        inactive.push(repo);
        break;
      case 'active':
        active.push(repo);
        break;
    }
  }

  return { active, inactive, inaccessible, evalFailed, notScored };
}
